import { useState } from 'react';
import { Edit2, TextalignLeft, Trash } from 'iconsax-react';

import ConfirmationDeleteDialog from '@shared/components/reusables/confrimation-delete-dialog';

import { useKnowledgeBaseFilters } from '../hooks/use-knowledge-base-filters';
import {
  knowledgeRichTextsApi,
  type KnowledgeRichText,
} from '../services/knowledge-content-api';
import { formatKnowledgeDate } from '../utils/format-date';
import { ContentCard } from './content-card';
import { ContentList } from './content-list';
import { ContentToolbar } from './content-toolbar';
import { RichTextDialog } from './rich-text-dialog';
import { SafeHtml } from './safe-html';

export function RichTextsTab({
  kbaseId,
  softActions = false,
}: {
  kbaseId: number;
  softActions?: boolean;
}) {
  const { search } = useKnowledgeBaseFilters();
  const [editing, setEditing] = useState<KnowledgeRichText | null | 'new'>(
    null
  );
  const [deleting, setDeleting] = useState<KnowledgeRichText | null>(null);

  const list = knowledgeRichTextsApi.useFetchInfinite({
    kbase: kbaseId,
    ...(search && { q: search }),
    page_size: 25,
  });
  const remove = knowledgeRichTextsApi.useDelete();

  return (
    <>
      <ContentToolbar
        placeholder={
          softActions ? 'Search in Rich Texts' : 'Search in your texts'
        }
        addLabel='Add Text'
        addVariant={softActions ? 'primaryPale' : 'primary'}
        onAdd={() => setEditing('new')}
      />
      <ContentList
        query={list}
        searching={Boolean(search)}
        emptyIcon={<TextalignLeft size={24} />}
        emptyTitle='No texts yet'
        emptyDescription='Write pages of context the agent should know, formatted the way you want it read.'
        renderItem={(richText) => (
          <ContentCard
            key={richText.id}
            data-rich-text-id={richText.id}
            title={richText.name}
            meta={formatKnowledgeDate(richText.created_at)}
            menu={[
              {
                label: 'Edit',
                icon: <Edit2 size={16} />,
                onSelect: () => setEditing(richText),
              },
              {
                label: 'Delete',
                icon: (
                  <Trash
                    size={16}
                    className='text-red-500'
                  />
                ),
                destructive: true,
                onSelect: () => setDeleting(richText),
              },
            ]}
          >
            <SafeHtml html={richText.content} />
          </ContentCard>
        )}
      />

      <RichTextDialog
        kbaseId={kbaseId}
        richText={editing === 'new' ? null : editing}
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
      />
      <ConfirmationDeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        cancelHandler={() => setDeleting(null)}
        confirmationHandler={() => {
          if (!deleting) return;
          remove.mutate(
            { id: deleting.id },
            { onSuccess: () => setDeleting(null) }
          );
        }}
      />
    </>
  );
}
